import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";
import PasswordInput from "../components/PasswordInput";
import { ROLE_LABEL } from "../utils/roleLabels";
import { isTokenExpired } from "../utils/jwt";
import { fetchGoogleConfig, renderGoogleButton } from "../utils/googleSignIn";

// Roles someone may ask for when signing up with Google. "superadmin" is
// deliberately absent: only a superadmin can hand that one out.
const SIGNUP_ROLE_OPTIONS = ["user", "admin", "sekretaris", "bendahara"].map((value) => ({
  value,
  label: ROLE_LABEL[value],
}));

const STAGE_HEAD = {
  form: { title: "Masuk ke Inventory", sub: "Divisi Kelistrikan" },
  register: { title: "Daftar akun" }, // its subtitle (name + Google email) is built in `head`
  pending: { title: "Pendaftaran diterima", sub: "Divisi Kelistrikan" },
};

// The backend answers a bad password login (and DRF's throttle) in English;
// everything else it says is already Indonesian, so only those two are
// rewritten and the rest passes through untouched.
function loginErrorMessage(status, data, fallback) {
  const detail = data?.detail;
  if (status === 429) {
    const wait = /(\d+)\s*second/i.exec(detail || "")?.[1];
    return wait
      ? `Terlalu banyak percobaan. Coba lagi dalam ${wait} detik.`
      : "Terlalu banyak percobaan. Coba lagi sebentar lagi.";
  }
  if (status === 401 && /no active account/i.test(detail || "")) {
    return "Username atau password salah.";
  }
  return detail || fallback;
}

const NETWORK_ERROR = "Terjadi kesalahan. Periksa koneksi internet, lalu coba lagi.";

function ErrorBox({ message }) {
  if (!message) return null;
  return (
    <div role="alert" className="login-error animate-fade-in">
      {message}
    </div>
  );
}

function Login() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Focusing a field on mount pops the keyboard on a phone and covers half of
  // the card before anyone has read it, so only desktops get autofocus.
  const [autoFocusUsername] = useState(
    () => !(window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
  );

  // Masuk dengan Google. `stage` swaps the card between the normal login form,
  // the sign-up panel (Google account we haven't seen before) and the
  // "waiting for admin" panel.
  const [googleCfg, setGoogleCfg] = useState(null); // null until /config answers
  const [googleReady, setGoogleReady] = useState(false); // Google's button is drawn
  const [googleError, setGoogleError] = useState(""); // shown under Google's button, not the form
  const [stage, setStage] = useState("form"); // "form" | "register" | "pending"
  const [googleCredential, setGoogleCredential] = useState("");
  const [signup, setSignup] = useState({ email: "", name: "" });
  const [pendingEmail, setPendingEmail] = useState(""); // only known when they just signed up
  const [pendingUsername, setPendingUsername] = useState(""); // likewise
  // The sign-up panel: the username they will type at the password login, and
  // the password they set right there (the Google token proves the email).
  const [signupUsername, setSignupUsername] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  const [requestedRole, setRequestedRole] = useState("user");
  const googleBtnRef = useRef(null);

  // Everything that happens once we hold an access token, for both password
  // and Google login: ask who we are, remember the token, land on the page
  // that fits the role. Reports its own failure through `reportError` (so the
  // message lands next to whichever button was used); returns whether it got
  // in. The token is only stored once /me accepts it, so a failure here never
  // leaves a half-logged-in session behind.
  const completeLogin = useCallback(
    async (accessToken, reportError) => {
      // Get current user info (role)
      const meRes = await fetch(`${API_BASE_URL}/api/auth/me/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!meRes.ok) {
        reportError("Gagal memuat data akun. Coba lagi.");
        return false;
      }

      const meData = await meRes.json();
      const role = meData.role;

      // Save access token for later API calls
      localStorage.setItem("accessToken", accessToken);

      // Redirect by role: the basic "user" role only has the Transaksi tab;
      // Sekretaris/Bendahara land on their Administrasi dashboard;
      // everyone else lands on the items list.
      if (role === "user") {
        navigate("/transactions", { replace: true });
      } else if (role === "sekretaris" || role === "bendahara") {
        navigate("/dashboard", { replace: true });
      } else if (role) {
        navigate("/items", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
      return true;
    },
    [navigate]
  );

  // Someone who is already signed in has no business on this page (back
  // button, bookmarked /login). Skip the form and send them where they'd land
  // after logging in. Silent on failure: the form is simply still there.
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token || isTokenExpired(token)) return;
    completeLogin(token, () => {}).catch(() => {});
  }, [completeLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // 1) Login: get tokens
      const tokenRes = await fetch(`${API_BASE_URL}/api/auth/token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!tokenRes.ok) {
        const errData = await tokenRes.json().catch(() => null);
        setError(loginErrorMessage(tokenRes.status, errData, "Login gagal. Coba lagi."));
        setLoading(false);
        return;
      }

      const tokenData = await tokenRes.json();

      // 2) Who are we + where to go
      await completeLogin(tokenData.access, setError);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(NETWORK_ERROR);
      setLoading(false);
    }
  };

  // Google handed us an ID token. The backend answers one of: tokens (go in),
  // "pending" (account exists, awaiting ACC), "needs_registration" (new
  // identity, nothing created yet), or an error.
  const handleGoogleCredential = useCallback(
    async (credential) => {
      setError("");
      setGoogleError("");
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/google/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential }),
        });
        const data = await res.json().catch(() => null);

        if (!res.ok) {
          setGoogleError(loginErrorMessage(res.status, data, "Login Google gagal. Coba lagi."));
        } else if (data?.access) {
          await completeLogin(data.access, setGoogleError);
        } else if (data?.status === "pending") {
          setPendingEmail("");
          setPendingUsername("");
          setStage("pending");
        } else if (data?.status === "needs_registration") {
          setGoogleCredential(credential);
          setSignup({ email: data.email || "", name: data.name || "" });
          setSignupUsername(data.suggested_username || "");
          setSignupPassword("");
          setSignupConfirm("");
          setRequestedRole("user");
          setStage("register");
        } else {
          setGoogleError("Respons login Google tidak dikenali.");
        }
      } catch (err) {
        console.error(err);
        setGoogleError(NETWORK_ERROR);
      } finally {
        setLoading(false);
      }
    },
    [completeLogin]
  );

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    // The backend checks all of this again (and more: common passwords, too
    // similar to the username); this only saves a round trip for the obvious.
    const username = signupUsername.trim();
    if (!/^[A-Za-z0-9._-]{3,150}$/.test(username)) {
      setError("Username 3-150 karakter: huruf, angka, titik, _ atau -.");
      return;
    }
    if (signupPassword.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    if (/^\d+$/.test(signupPassword)) {
      setError("Password tidak boleh hanya angka.");
      return;
    }
    if (signupPassword !== signupConfirm) {
      setError("Password tidak sama.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/google/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credential: googleCredential,
          requested_role: requestedRole,
          username,
          password: signupPassword,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(loginErrorMessage(res.status, data, "Pendaftaran gagal. Coba lagi."));
        return;
      }
      setGoogleCredential("");
      setSignupPassword("");
      setSignupConfirm("");
      setPendingEmail(signup.email);
      setPendingUsername(username);
      setStage("pending");
    } catch (err) {
      console.error(err);
      setError(NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const backToForm = () => {
    setError("");
    setGoogleError("");
    setGoogleCredential("");
    setSignupPassword("");
    setSignupConfirm("");
    setStage("form");
  };

  useEffect(() => {
    let cancelled = false;
    fetchGoogleConfig().then((cfg) => {
      if (!cancelled) setGoogleCfg(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // (Re)draw Google's button whenever the form is on screen: switching to the
  // sign-up / pending panel unmounts its container.
  useEffect(() => {
    if (stage !== "form" || !googleCfg?.enabled || !googleBtnRef.current) return undefined;
    let cancelled = false;
    setGoogleReady(false);
    renderGoogleButton({
      clientId: googleCfg.client_id,
      element: googleBtnRef.current,
      onCredential: handleGoogleCredential,
      shape: "rectangular",
    })
      .then(() => {
        if (!cancelled) setGoogleReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleError("Tombol Google gagal dimuat. Cek koneksi internet, lalu muat ulang halaman.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stage, googleCfg, handleGoogleCredential]);

  // Google stays in the layout from the first paint (as a placeholder) until
  // /config says it is switched off, so the card doesn't grow after it has
  // already faded in.
  const showGoogle = googleCfg?.enabled !== false;

  // Register and pending are a page of their own in the design: no status
  // strips, a tighter card.
  const bare = stage !== "form";

  const head =
    stage === "register"
      ? {
          title: STAGE_HEAD.register.title,
          sub: (
            <>
              {signup.name ? `Halo, ${signup.name} · ` : ""}
              <span className="login-sub-strong">{signup.email}</span>
            </>
          ),
        }
      : STAGE_HEAD[stage];

  return (
    <div className={`login-page${bare ? " is-bare" : ""}`}>
      {!bare && (
        <div className="login-bar login-bar-top" aria-hidden="true">
          <span className="login-status">
            <span className="login-dot" />
            SYSTEM ONLINE · 220V
          </span>
          <span>INVENTORY / AUTH</span>
        </div>
      )}

      <div className="login-panel animate-fade-slide-up">
        <div className="login-stripe" aria-hidden="true" />
        <div className="login-body">
          <div className="login-head">
            <div className="login-logo-crop">
              <img src="/logo.png" alt="Lisokom" className="login-logo" />
            </div>
            <span className="login-badge">INV-01</span>
          </div>

          <div className="login-titles">
            <h1 className="login-title">{head.title}</h1>
            <p className="login-sub">{head.sub}</p>
          </div>

          {/* Keyed on the stage so swapping panels fades instead of snapping. */}
          <div key={stage} className="login-stage animate-fade-in">
            {stage === "form" && (
              <>
                {showGoogle && (
                  <div className="login-google-block">
                    {/* GIS writes into the ref'd div itself, so it must stay
                        childless; the placeholder is its sibling. */}
                    <div className="login-google">
                      <div ref={googleBtnRef} className="login-google-slot" />
                      {!googleReady && !googleError && (
                        <div aria-hidden="true" className="login-google-skeleton animate-pulse" />
                      )}
                    </div>
                    <span className="login-hint">
                      Wajib pakai akun Gmail. Akun baru otomatis terdaftar.
                    </span>
                    {googleError && <ErrorBox message={googleError} />}
                  </div>
                )}

                {showGoogle && <div className="login-divider">ATAU PAKAI USERNAME</div>}

                <form onSubmit={handleSubmit} className="login-form">
                  <div className="login-fields">
                    <div className="login-field">
                      <label htmlFor="username" className="login-label">
                        USERNAME
                      </label>
                      <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          setError("");
                        }}
                        required
                        autoFocus={autoFocusUsername}
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        enterKeyHint="next"
                        className="login-input"
                        placeholder="nama.pengguna"
                      />
                    </div>

                    <div className="login-field">
                      <label htmlFor="password" className="login-label">
                        PASSWORD
                      </label>
                      <PasswordInput
                        variant="login"
                        id="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        required
                        autoComplete="current-password"
                        enterKeyHint="go"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <ErrorBox message={error} />

                  <button
                    type="submit"
                    disabled={loading}
                    className="login-submit login-submit-outline"
                  >
                    {loading ? "Memeriksa…" : "Masuk"}
                  </button>
                </form>
              </>
            )}

            {stage === "register" && (
              <form onSubmit={handleRegister} className="login-form">
                <div className="login-fields">
                  <div className="login-field">
                    <label htmlFor="signup-username" className="login-label">
                      USERNAME
                    </label>
                    <input
                      id="signup-username"
                      type="text"
                      value={signupUsername}
                      onChange={(e) => {
                        setSignupUsername(e.target.value);
                        setError("");
                      }}
                      required
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      className="login-input"
                      placeholder="nama.pengguna"
                    />
                    <span className="login-field-hint">Huruf, angka, titik, _ atau -</span>
                  </div>

                  <div className="login-field">
                    <label htmlFor="signup-password" className="login-label">
                      PASSWORD
                    </label>
                    <PasswordInput
                      variant="login"
                      id="signup-password"
                      value={signupPassword}
                      onChange={(e) => {
                        setSignupPassword(e.target.value);
                        setError("");
                      }}
                      required
                      autoComplete="new-password"
                      placeholder="Min. 8 karakter"
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="signup-confirm" className="login-label">
                      ULANGI PASSWORD
                    </label>
                    <PasswordInput
                      variant="login"
                      id="signup-confirm"
                      value={signupConfirm}
                      onChange={(e) => {
                        setSignupConfirm(e.target.value);
                        setError("");
                      }}
                      required
                      autoComplete="new-password"
                      placeholder="Ketik ulang"
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="signup-role" className="login-label">
                      ROLE
                    </label>
                    <div className="login-select-wrap">
                      <select
                        id="signup-role"
                        value={requestedRole}
                        onChange={(e) => setRequestedRole(e.target.value)}
                        className="login-input login-select"
                      >
                        {SIGNUP_ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="login-select-chev" aria-hidden="true" />
                    </div>
                    <span className="login-field-hint">Disetujui superadmin.</span>
                  </div>
                </div>

                <ErrorBox message={error} />

                <div className="login-actions">
                  <button type="submit" disabled={loading} className="login-submit">
                    {loading ? "Mendaftar…" : "Daftar"}
                  </button>
                  <button
                    type="button"
                    onClick={backToForm}
                    disabled={loading}
                    className="login-secondary"
                  >
                    Batal
                  </button>
                </div>
              </form>
            )}

            {stage === "pending" && (
              <div className="login-pending">
                <div className="login-pending-icon">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                  </svg>
                </div>
                <div className="login-copy">
                  <h2 className="login-h2">Menunggu ACC superadmin</h2>
                  <p>
                    {pendingUsername ? (
                      <>
                        Setelah di-ACC, masuk dengan username <strong>{pendingUsername}</strong>{" "}
                        dan password kamu, atau lewat Google
                        {pendingEmail && <> ({pendingEmail})</>}.
                      </>
                    ) : (
                      "Setelah di-ACC, masuk lewat tombol Google."
                    )}
                  </p>
                  <p>Lama belum di-ACC? Kabari superadmin.</p>
                </div>
                <button type="button" onClick={backToForm} className="login-secondary">
                  Kembali ke halaman login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!bare && (
        <div className="login-bar login-bar-bottom">
          <span>© {new Date().getFullYear()} Lisokom</span>
          <span>Lupa password? Hubungi admin</span>
        </div>
      )}
    </div>
  );
}

export default Login;
